import React, { useMemo, useState } from 'react';

const panelStyle = {
    border: '1px solid #fde68a',
    backgroundColor: '#fffbeb',
    borderRadius: '8px',
    padding: '16px',
    marginTop: '24px',
};

const titleStyle = {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    color: '#78350f',
};

const subtitleStyle = {
    fontSize: '13px',
    color: '#92400e',
};

const sectionStyle = {
    marginTop: '12px',
};

const sectionHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '8px 10px',
    backgroundColor: '#fef3c7',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#78350f',
    userSelect: 'none',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    backgroundColor: '#fff',
    marginTop: '8px',
};

const thStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 10px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '12px',
    color: '#374151',
};

const tdStyle = {
    border: '1px solid #e5e7eb',
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: '12px',
    color: '#1f2937',
};

const reasonPillStyle = (reason) => {
    const palette = {
        'No matching inbound booking found': { bg: '#fee2e2', fg: '#991b1b' },
        'No at-location signal on either leg': { bg: '#e0e7ff', fg: '#3730a3' },
        'No at-location signal (inbound leg not paired)': { bg: '#fef3c7', fg: '#78350f' },
    };
    const c = palette[reason] || { bg: '#f3f4f6', fg: '#374151' };
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
    };
};

const groupPillStyle = (group) => {
    const isPre = group === 'preAdvised';
    return {
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor: isPre ? '#dcfce7' : '#fee2e2',
        color: isPre ? '#166534' : '#991b1b',
        whiteSpace: 'nowrap',
    };
};

const formatTime = (value) => {
    if (!value) return '–';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

const formatRoute = (origin, destination) => {
    if (!origin && !destination) return '–';
    return `${origin || '?'} → ${destination || '?'}`;
};

const groupLabel = (group) => (group === 'preAdvised' ? 'Pre-Advised' : 'Non Pre-Advised');

const CollapsibleSection = ({ title, count, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div style={sectionStyle}>
            <div
                style={sectionHeaderStyle}
                onClick={() => setOpen((v) => !v)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v); }}
            >
                <span>{title} ({count})</span>
                <span style={{ fontSize: '14px' }}>{open ? '\u25BE' : '\u25B8'}</span>
            </div>
            {open && children}
        </div>
    );
};

const TransferSlaAttentionPanel = ({ unpairedTransfers = [], unassistedTransfers = [], hideBreakdown = false }) => {
    const totalIssues = unpairedTransfers.length + unassistedTransfers.length;

    const groupCounts = useMemo(() => {
        const counts = { preAdvised: 0, nonPreAdvised: 0 };
        [...unpairedTransfers, ...unassistedTransfers].forEach((r) => {
            const key = r.group === 'preAdvised' ? 'preAdvised' : 'nonPreAdvised';
            counts[key] += 1;
        });
        return counts;
    }, [unpairedTransfers, unassistedTransfers]);

    if (totalIssues === 0) return null;

    return (
        <section style={panelStyle}>
            <div>
                <h3 style={titleStyle}>Transit pax needing attention</h3>
                <p style={{ ...subtitleStyle, marginTop: 4, marginBottom: 0 }}>
                    {unpairedTransfers.length} unpaired
                    {' · '}
                    {unassistedTransfers.length} unassisted
                    {' · '}
                    {groupCounts.preAdvised} pre-advised / {groupCounts.nonPreAdvised} non pre-advised
                </p>
            </div>

            {hideBreakdown ? (
                <p style={{ ...subtitleStyle, marginTop: 12, marginBottom: 0 }}>
                    Per-passenger breakdown is hidden for your role.
                </p>
            ) : (
                <>
                    {unpairedTransfers.length > 0 && (
                        <CollapsibleSection
                            title="Unpaired transfers (no matching inbound booking)"
                            count={unpairedTransfers.length}
                            defaultOpen
                        >
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Date</th>
                                            <th style={thStyle}>Group</th>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Outbound</th>
                                            <th style={thStyle}>Outbound Route</th>
                                            <th style={thStyle}>Outbound STD</th>
                                            <th style={thStyle}>Inbound (from booking)</th>
                                            <th style={thStyle}>Expected Inbound ID</th>
                                            <th style={thStyle}>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unpairedTransfers.map((row, idx) => (
                                            <tr key={`${row.date}-${row.bookingId}-${idx}`}>
                                                <td style={tdStyle}>{row.date || '–'}</td>
                                                <td style={tdStyle}>
                                                    <span style={groupPillStyle(row.group)}>{groupLabel(row.group)}</span>
                                                </td>
                                                <td style={tdStyle}>{row.name || '–'}</td>
                                                <td style={tdStyle}>{row.outboundFlight || '–'}</td>
                                                <td style={tdStyle}>{formatRoute(row.outboundOrigin, row.outboundDestination)}</td>
                                                <td style={tdStyle}>{formatTime(row.outboundDepartureTime)}</td>
                                                <td style={tdStyle}>
                                                    {row.inboundFlight || '–'}
                                                    {(row.inboundOrigin || row.inboundDestination) && (
                                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                            {formatRoute(row.inboundOrigin, row.inboundDestination)} {row.inboundDepartureDate ? `· ${row.inboundDepartureDate}` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ ...tdStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
                                                    {row.expectedInboundId || '–'}
                                                </td>
                                                <td style={tdStyle}><span style={reasonPillStyle(row.reason)}>{row.reason}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CollapsibleSection>
                    )}

                    {unassistedTransfers.length > 0 && (
                        <CollapsibleSection
                            title="Unassisted transit pax (no at-location signal on either leg)"
                            count={unassistedTransfers.length}
                            defaultOpen
                        >
                            <div style={{ overflowX: 'auto' }}>
                                <table style={tableStyle}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Date</th>
                                            <th style={thStyle}>Group</th>
                                            <th style={thStyle}>Name</th>
                                            <th style={thStyle}>Inbound</th>
                                            <th style={thStyle}>Outbound</th>
                                            <th style={thStyle}>Outbound STD</th>
                                            <th style={thStyle}>Rejection</th>
                                            <th style={thStyle}>Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {unassistedTransfers.map((row, idx) => (
                                            <tr key={`${row.date}-${row.bookingId}-${idx}`}>
                                                <td style={tdStyle}>{row.date || '–'}</td>
                                                <td style={tdStyle}>
                                                    <span style={groupPillStyle(row.group)}>{groupLabel(row.group)}</span>
                                                </td>
                                                <td style={tdStyle}>{row.name || '–'}</td>
                                                <td style={tdStyle}>
                                                    {row.inboundFlight || '–'}
                                                    {(row.inboundOrigin || row.inboundDestination) && (
                                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                            {formatRoute(row.inboundOrigin, row.inboundDestination)} {row.inboundDepartureDate ? `· ${row.inboundDepartureDate}` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    {row.outboundFlight || '–'}
                                                    {(row.outboundOrigin || row.outboundDestination) && (
                                                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                            {formatRoute(row.outboundOrigin, row.outboundDestination)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>{formatTime(row.outboundDepartureTime)}</td>
                                                <td style={tdStyle}>{row.paxRejectionReason || '–'}</td>
                                                <td style={tdStyle}><span style={reasonPillStyle(row.reason)}>{row.reason}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CollapsibleSection>
                    )}
                </>
            )}
        </section>
    );
};

export default TransferSlaAttentionPanel;
